/* eslint-disable @typescript-eslint/consistent-type-imports */
import React, { ReactNode } from 'react';
/* eslint-disable check-i18n-id-prefix/i18n-id-prefix-with-package-name-defineMessages */
import { defineMessages, FormattedMessage } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { token } from '@atlaskit/tokens';
import { css } from '@atlaskit/css';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import type {
	FlagsStateContainer,
	FlagDescriptor,
} from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { User } from '@atlaskit/user-picker';

import { PartyIcon } from '../assets/PartyIcon.tsx';
import { SuccessFlagDescription } from './SuccessFlagDescription.tsx';

// We want the flag to stay for about 8000ms + view page load time 3000ms (according to FMP)
export const PUBLISH_SUCCESS_FLAG_AUTO_CLOSE_TIME = 11000;

type ShowPublishSuccessFlagsWithSocialAndCopyLinkProps = {
	spaceName?: string;
	flags: FlagsStateContainer;
	title: string | null;
	intl: IntlShape;
	copyContentLink?: () => void;
	createAnalyticsEvent?: CreateUIAnalyticsEvent;
	suggestedUsers?: User[] | null;
	showShareModal?: () => void;
};

const i18n = defineMessages({
	publishSuccessTitle: {
		id: 'editor-features.success.publish.title',
		description: 'Success title message when a page is published',
		defaultMessage: 'Published!',
	},
	publishSuccessTitleWithSocial: {
		id: 'editor-features.publish.success.title.with.social',
		description: 'Success title message when a page is published with collaborator avatar',
		defaultMessage: 'Page successfully published',
	},
	copyLinkCTA: {
		id: 'editor-features.user.copy-your-page',
		defaultMessage: 'Copy link',
		description:
			'This text is shown on successfully published flag to indicate that they can click this text to copy the URL to the current content (page/blog/etc...)',
	},
	copiedCTA: {
		id: 'editor-features.user.copied-page',
		defaultMessage: 'Copied',
		description:
			'This text is shown when a user has clicked a CTA and has already copied the URL to the current page',
	},
	sharePageCTA: {
		id: 'editor-features.publish.success.share-page',
		description: 'Call to action for sharing the page after publishing',
		defaultMessage: 'Share page',
	},
});

const copiedLinkStyles = css({
	color: token('color.text.disabled'),
});

type FlagAction = {
	content: React.ReactNode;
	onClick?: () => void;
};

/*
 * Show success flags for publishing with social sharing and copy link options
 */
export const showPublishSuccessFlagsWithSocialAndCopyLink = async ({
	spaceName,
	flags,
	title,
	intl,
	copyContentLink,
	createAnalyticsEvent,
	suggestedUsers = [],
	showShareModal,
}: ShowPublishSuccessFlagsWithSocialAndCopyLinkProps) => {
	let isLinkCopied = false;
	let flagId: string | number;

	let flagTitle = i18n.publishSuccessTitleWithSocial;

	const handleCopyClick = () => {
		if (isLinkCopied || !copyContentLink) return;

		isLinkCopied = true;

		if (createAnalyticsEvent) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: 'publishFlagCopyPageCTA',
					source: 'publishSuccessFlag',
					attributes: {
						isFirstTimePublish: true,
						isSocialFlag: true,
					},
				},
			}).fire();
		}

		void copyContentLink();

		// Update flag with "Copied" state
		flags.updateFlag(flagId, {
			actions: createActions(),
		});

		// Reset after 4 seconds
		setTimeout(() => {
			isLinkCopied = false;
			flags.updateFlag(flagId, {
				actions: createActions(),
			});
		}, 4000);
	};

	const handleShareClick = () => {
		if (createAnalyticsEvent) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'publishFlagSharePageCTA',
					source: 'publishSuccessFlag',
					attributes: {
						isFirstTimePublish: true,
					},
				},
			}).fire();
		}

		showShareModal?.();
	};

	const createActions = (): FlagAction[] => {
		const actions: FlagAction[] = [];

		if (copyContentLink) {
			if (!!showShareModal) {
				actions.push({
					content: <FormattedMessage {...i18n.sharePageCTA} />,
					onClick: handleShareClick,
				});
			}

			actions.push({
				content: isLinkCopied ? (
					<div css={copiedLinkStyles}>
						<FormattedMessage {...i18n.copiedCTA} />
					</div>
				) : (
					<FormattedMessage {...i18n.copyLinkCTA} />
				),
				onClick: isLinkCopied ? undefined : handleCopyClick,
			});
		}

		return actions;
	};

	// Create description content
	const createDescription = (): ReactNode => {
		return (
			<SuccessFlagDescription
				intl={intl}
				title={title}
				spaceName={spaceName}
				suggestedUsers={suggestedUsers}
				showSocial={!!copyContentLink}
			/>
		);
	};

	let flagProps: Partial<FlagDescriptor> = {};

	if (copyContentLink) {
		flagProps = {
			type: 'custom',
			customIcon: <PartyIcon />,
			description: createDescription(),
			actions: createActions(),
		};
	} else {
		// Fallback when no copy functions provided
		flagProps = {
			description: createDescription(),
		};
		flagTitle = i18n.publishSuccessTitle;
	}

	const publishSuccessFlag = await flags.showFlag({
		type: 'success-circle',
		title: intl.formatMessage(flagTitle),
		close: 'never',
		messageId: 'confluence-frontend-editor-features-publish-success-flag',
		messageType: 'transactional',
		...flagProps,
	});

	flagId = publishSuccessFlag.id;

	setTimeout(() => flags.hideFlag(publishSuccessFlag.id), PUBLISH_SUCCESS_FLAG_AUTO_CLOSE_TIME);
};
